import api from "@/services/api";
import config from "@/config";

const cookies = require("js-cookie");

/**
 * De overdracht vanuit het adminpaneel (ADR-0018).
 *
 * Het adminpaneel staat op een ander registrable domain dan deze app
 * (`inschrijvingsmanager.defittemedewerker.nl` tegenover `{pagina}.dfm-app.nl`),
 * dus de sessiecookie kan er niet bij. Wie daar op "Aanmeldingen bekijken" klikt
 * krijgt een eenmalig token van 60 seconden mee, en hier wordt dat ingewisseld
 * voor een eigen sessie.
 *
 * Drie dingen aan de vorm zijn met opzet zo:
 *
 * - **Het token staat in de fragment, niet in de query.** Een fragment gaat
 *   nooit naar een server, dus het staat in geen enkel access-log tussen het
 *   adminpaneel en dit tabblad. Een queryparameter zou dat wel doen.
 * - **Het wordt bij het laden van deze module uit de adresbalk gehaald**, niet
 *   pas bij het inwisselen. Twee redenen: mislukt de inwisseling, dan is het
 *   credential alsnog uit de balk verdwenen -- en Vue Router legt bij zijn
 *   aanmaak de dan geldende URL vast en zet die bij de eerste navigatie terug.
 *   Strippen ná die aanmaak levert dus een schone balk die meteen weer vuil
 *   wordt. Daarom **staat de import hiervan in `main.js` boven die van de
 *   router**, en bewaakt `npm run test:handover` dat dat zo blijft.
 * - **De cookies krijgen geen `expires`.** Dit is een overgedragen sessie, geen
 *   "onthoud mij": hij hoort te eindigen als de browser dat doet.
 */
const PARAM = "ho";

/** Het token uit de URL, één keer gelezen en meteen weggehaald. */
const captured = captureToken();

function captureToken() {
  if (typeof window === "undefined" || !window.location) return null;

  const raw = (window.location.hash || "").replace(/^#/, "");
  if (raw === "") return null;

  const params = new URLSearchParams(raw);
  const token = params.get(PARAM);
  if (!token || token.trim() === "") return null;

  params.delete(PARAM);
  const rest = params.toString();
  window.history.replaceState(
    null,
    "",
    window.location.pathname + window.location.search + (rest ? `#${rest}` : ""),
  );

  return token;
}

/** Droeg deze URL een overdrachtstoken? Zo niet, dan hoeft niets te wachten. */
export function hasHandoverToken() {
  return captured !== null;
}

/**
 * Wisselt het token in en zet de sessiecookies. Levert `true` bij succes.
 *
 * Faalt hij, dan gebeurt er verder niets: de app loopt zijn gewone route en
 * stuurt een bezoeker zonder sessie naar het inlogscherm. Dat is ook wat er
 * hoort te gebeuren als het token verlopen of al gebruikt is.
 */
export async function consumeHandoverToken() {
  if (captured === null) return false;

  try {
    const res = await api.post(`${config.url.api}/v1/auth/handover/exchange`, {
      token: captured,
    });

    if (!res.data || !res.data.success || !res.data.data) return false;

    const cookieOptions = { domain: config.cookiedomain };
    cookies.set("fmw_refresh_token", res.data.data.refresh_token, cookieOptions);
    cookies.set("fmw_access_token", res.data.data.access_token, cookieOptions);

    return true;
  } catch (e) {
    return false;
  }
}
